import { useMemo, useState } from 'react';
import { ProductQualityAudit } from '@data';
import { useProductQualityAudit, useProductQualityAudits } from '@data/hooks/products';
import { qualityPeriodLabel } from './sources/quality';

const isOlder = (audit: { year: number; quarter: number }, than: { year: number; quarter: number }): boolean =>
  audit.year < than.year || (audit.year === than.year && audit.quarter < than.quarter);

/** The picked earlier quarter, the quarters it can be picked from, and which read the body renders. */
export const useQualityComparison = (productId: string | undefined, report: ProductQualityAudit | null) => {
  const label = report ? qualityPeriodLabel(report) : '';
  const [compareTo, setCompareTo] = useState<string | undefined>(undefined);
  const scope = `${productId ?? ''}|${label}`;
  const [comparedFor, setComparedFor] = useState(scope);
  // A comparison belongs to the product and quarter it was picked for, so showing another turns it off.
  if (comparedFor !== scope) {
    setComparedFor(scope);
    setCompareTo(undefined);
  }

  const { data: index } = useProductQualityAudits(productId);
  const base = useProductQualityAudit(productId, report?.year, report?.quarter);
  const compared = useProductQualityAudit(productId, report?.year, report?.quarter, compareTo);

  const isShown = !!compareTo && !compared.isPending && !compared.isPlaceholderData && !compared.isError;
  // When the quarter itself cannot be read, that is the only failure worth saying.
  const isFailed = !!compareTo && compared.isError && !base.isError;
  const older = useMemo(
    () => (report ? (index?.items ?? []).filter(each => isOlder(each, report)) : []),
    [index, report],
  );

  return { compareTo, setCompareTo, older, isShown, isFailed, shown: isShown ? compared : base };
};
